import { Box, Button, Link, Stack, Typography } from "@mui/material";
import type { GridAutosizeOptions } from "@mui/x-data-grid-pro";
import { DataGridPro, GridColDef, GridRowClassNameParams, useGridApiRef } from "@mui/x-data-grid-pro"; // @ts-ignore for some reason, it can'f find dependency
import { GridProSlotProps } from "@mui/x-data-grid-pro/models/gridProSlotProps";
import { RatelistStatus, RatelistType } from "@sinch/core/entities/serverEnums";
import { useFormat } from "@sinch/core/hooks/format";
import { useQueryHandler } from "@sinch/hooks/useQueryHandler";
import { capitalize } from "@sinch/utils/string";
import { QuickSearchToolbar } from "@sinch/views/Rates/QuickSearchToolbar";
import { useRememberDataGridState } from "@sinch/views/Rates/UseRememberDataGridState";
import { isEmpty, isNil, pluck, propEq } from "ramda";
import { isNotNilOrEmpty } from "ramda-adjunct";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { match } from "ts-pattern";

import { EditActionItem } from "./EditActionItem";
import { RevisionDialog } from "./MenuActions/RevisionsMenuItem";
import { RateRowMenu } from "./RateRowMenu";
import { useRateRevision } from "@sinch/components/application/Rate/tools";
import { useRatelist } from "@sinch/components/application/Rate/useRatelist";
import { useRatelistType } from "@sinch/components/application/Rate/useRatelistType";
import { AddRatelistDialog } from "@sinch/components/application/RatelistTable/AddRatelistDialog";
import { RatelistsTableRatelists } from "@sinch/components/application/RatelistTable/queries.graphql";
import {
  RatelistsTableRatelistsQuery,
  RatelistsTableRatelistsQueryVariables,
} from "@sinch/components/application/RatelistTable/queries.types";
import { ExtendableRow } from "@sinch/components/ExtendableRow/ExtendableRow";
import { Pill } from "@sinch/components/Pill/Pill";
import { useSettings } from "@sinch/core/providers/AppSettings";

const autosizeOptions: GridAutosizeOptions = {
  columns: ["name", "actions", "modified", "modifier", "status"],
  includeHeaders: true,
  includeOutliers: true,
  expand: false,
  outliersFactor: 2,
  disableColumnVirtualization: true,
};

const pinnedColumns = {
  right: ["actions"],
};

export interface RatelistTableRowsData {
  id: string;
  name: string;
  description: string;
  status: RatelistStatus;
  default: boolean;
  modified: Date;
  modifier: {
    id: string;
    name: string | null;
    surname: string | null;
  } | null;
  companies: {
    id: string;
    name: string;
  }[];
  isBeingUsed: boolean;
  hasLineage: boolean;
  version: number;
  isCompanySpecific?: boolean;
  isDefaultForCompany?: boolean;
  isGlobal: boolean;
}

export const RatelistTable = ({ type, companyId }: { type: RatelistType; companyId?: string }) => {
  const { t } = useTranslation(["common", "draft"]);
  const { locale } = useSettings();
  const { date, name } = useFormat();
  const { getRatelistTypeEditUri, getRatelistTypeViewUri } = useRatelistType();
  const apiRef = useGridApiRef();
  const { getRevisionNumber } = useRateRevision();

  const [revisionOpenId, setRevisionOpenId] = useState<string | null>(null);
  const [addDialogOpen, setAddDialogOpen] = useState(false);

  const {
    loading,
    data: globalData,
    refetch,
    previousData,
  } = useQueryHandler<RatelistsTableRatelistsQuery, RatelistsTableRatelistsQueryVariables>(RatelistsTableRatelists, {
    filter: { type: { EQ: type }, companyId: companyId ? { EQ: companyId } : undefined },
  });

  const { loading: mutating } = useRatelist();

  const ratelists = useMemo(() => {
    if (globalData) {
      return globalData.ratelists.filter(propEq("type", type));
    }
    return null;
  }, [globalData, type]);

  useEffect(() => {
    if (ratelists) {
      apiRef.current?.setRows(
        ratelists
          .map((component) => {
            const isGlobal = companyId ? component.companies.length === 0 : false;
            const isCompanySpecific = companyId
              ? component.companies.length === 1 && component.companies[0]?.id === companyId
              : false;
            const isDefaultForCompany = companyId ? component.defaultCompanies.some((c) => c.id === companyId) : false;

            return {
              id: component.id,
              name: component.name,
              description: component.description || "",
              status: component.status,
              default: component.default,
              modified: component.modified,
              modifier: component.modifier,
              companies: component.companies,
              isBeingUsed: component.isBeingUsed,
              hasLineage: component.version > 1,
              version: component.version,
              isCompanySpecific,
              isDefaultForCompany,
              isGlobal,
            };
          })
          .sort((a, b) => {
            const aArchived = a.status === RatelistStatus.Archived ? 1 : 0;
            const bArchived = b.status === RatelistStatus.Archived ? 1 : 0;
            if (aArchived !== bArchived) return aArchived - bArchived;
            return a.name.localeCompare(b.name, locale);
          })
      );
    }
  }, [apiRef, ratelists, companyId]);

  const companiesColumn: GridColDef<RatelistTableRowsData> = useMemo(
    () => ({
      field: "companies",
      sortComparator: (v1, v2) => (v1.length > v2.length ? 1 : -1),
      headerName: t("CompanyForm.companies"),
      flex: 1,
      display: "flex",
      maxWidth: 400,
      renderCell: ({ row }) =>
        isEmpty(row.companies) ? (
          <Typography sx={({ palette }) => ({ color: palette.grey["500"] })}>
            {t("Ratelist.forAllCompanies")}
          </Typography>
        ) : (
          <ExtendableRow>
            {row.companies.flatMap(({ id, name }, index) => [
              <Link key={id} href={`/admin/companies/view/${id}`}>
                {name}
              </Link>,
              index < row.companies.length - 1 && <span key={`sep-${index}`}>, </span>,
            ])}
          </ExtendableRow>
        ),
    }),
    [t]
  );

  const columns: GridColDef<RatelistTableRowsData>[] = useMemo(
    () => [
      {
        field: "name",
        headerName: t("name"),
        maxWidth: 400,
        flex: 2,
        display: "flex",
        renderCell: ({ row, value }) => (
          <Link color="textPrimary" href={getRatelistTypeViewUri(type)(row.id)}>
            {value}
          </Link>
        ),
      },
      { field: "description", headerName: t("description"), flex: 2 },
      companiesColumn,
      {
        field: "version",
        headerName: t("revision"),
        flex: 1,
        maxWidth: 80,
        align: "center",
        display: "flex",
        renderCell: ({ row }) => (
          <Link onClick={() => setRevisionOpenId(row.id)}>{getRevisionNumber(row.version)}</Link>
        ),
      },
      {
        field: "modified",
        headerName: t("modified"),
        flex: 1,
        maxWidth: 400,
        valueGetter: (_, row) => date.full(row.modified),
      },
      {
        field: "modifier",
        headerName: t("modifier"),
        sortable: false,
        flex: 1,
        maxWidth: 400,
        display: "flex",
        renderCell: ({ row }) => (
          <Link href={`/admin/admins/view/${row.modifier?.id}`}>
            {name.full({
              name: row.modifier?.name,
              surname: row.modifier?.surname,
            })}
          </Link>
        ),
      },
      {
        field: "status",
        resizable: false,
        headerName: "",
        display: "flex",
        flex: 1,
        sortComparator: (_v1, _v2, params1, params2) => {
          const row1 = apiRef.current?.getRow(params1.id) as RatelistTableRowsData | undefined;
          const row2 = apiRef.current?.getRow(params2.id) as RatelistTableRowsData | undefined;
          if (!row1 || !row2) return 0;

          const isArchived = (row: RatelistTableRowsData) => row.status === RatelistStatus.Archived;
          return Number(isArchived(row1)) - Number(isArchived(row2));
        },
        valueGetter: (_value, { default: isDefault, status, isDefaultForCompany }) => {
          if (companyId && isDefaultForCompany) {
            return capitalize(t("CompanyRatelist.defaultForCompany"));
          } else if (isDefault) {
            return capitalize(t("default"));
          } else {
            return match(status)
              .with(RatelistStatus.Active, () => t("active"))
              .with(RatelistStatus.Draft, () => t("RateBuilder.rateDraft"))
              .with(RatelistStatus.Archived, () => t("RateBuilder.archived"))
              .exhaustive();
          }
        },
        renderCell: ({ row: { status, default: isDefault, isDefaultForCompany } }) => (
          <Stack direction="row" spacing={0.5}>
            {companyId && isDefaultForCompany ? (
              <Pill color="info" label={t("CompanyRatelist.defaultForCompany")} size="small" />
            ) : isDefault ? (
              <Pill color="info" label={capitalize(t("default"))} size="small" />
            ) : (
              match(status)
                .with(RatelistStatus.Active, () => <Pill color="success" label={t("active")} size="small" />)
                .with(RatelistStatus.Draft, () => (
                  <Pill color="warning" label={t("RateBuilder.rateDraft")} size="small" />
                ))
                .with(RatelistStatus.Archived, () => (
                  <Pill color="warning" label={t("RateBuilder.archived")} size="small" />
                ))
                .exhaustive()
            )}
          </Stack>
        ),
      },
      {
        resizable: false,
        field: "actions",
        type: "actions",
        minWidth: 80,
        maxWidth: 80,
        display: "flex",
        getActions: (params) => [
          <EditActionItem key="edit" href={getRatelistTypeEditUri(type)(params.row.id)} />,
          <RateRowMenu
            key="menu"
            companyId={companyId}
            hasLineage={params.row.hasLineage}
            id={params.row.id}
            isBeingUsed={params.row.isBeingUsed}
            isCompanySpecific={params.row.isCompanySpecific}
            isDefault={params.row.default}
            isDefaultForCompany={params.row.isDefaultForCompany}
            isGlobal={params.row.isGlobal}
            onPerformedAction={() => refetch()}
            status={params.row.status}
            type={type}
          />,
        ],
      },
    ],
    [companyId, companiesColumn, date, name, refetch, t, type, getRatelistTypeEditUri, getRevisionNumber]
  );

  const rowClassName = useCallback(
    (params: GridRowClassNameParams) =>
      params.indexRelativeToCurrentPage % 2 === 0 ? "MuiDataGrid-row-even" : "MuiDataGrid-row-odd",
    []
  );

  const createNewUrl = companyId
    ? `${getRatelistTypeEditUri(type)()}?companyId=${companyId}`
    : getRatelistTypeEditUri(type)();

  const slotProps = useMemo<GridProSlotProps>(
    () => ({
      loadingOverlay: {
        variant: loading ? "skeleton" : "linear-progress",
        noRowsVariant: loading ? "skeleton" : "linear-progress",
      },
      toolbar: {
        showQuickFilter: true,
        children: (
          <Stack direction="row" spacing={1}>
            {companyId && (
              <Button color="primary" onClick={() => setAddDialogOpen(true)} size="medium" variant="outlined">
                {t("CompanyRatelist.addExisting")}
              </Button>
            )}
            <Button
              color="primary"
              onClick={() => (globalThis.location.href = createNewUrl)}
              size="medium"
              variant="contained"
            >
              {companyId ? t("Ratelist.addForCompanyButton") : t("Ratelist.addButton")}
            </Button>
          </Stack>
        ),
      },
    }),
    [companyId, createNewUrl, loading, t]
  );

  const slots = useMemo(() => ({ toolbar: QuickSearchToolbar }), []);

  const stateKey = companyId
    ? `RatelistTableState:DataGridState:${type}:${companyId}`
    : `RatelistTableState:DataGridState:${type}`;
  const dataGridState = useRememberDataGridState(apiRef, stateKey);

  useEffect(() => {
    if (!loading && ratelists && (isNil(dataGridState) || isNotNilOrEmpty(previousData?.ratelists))) {
      // requestAnimationFrame ensures the DOM has painted
      const timer = setTimeout(() => {
        apiRef.current?.autosizeColumns(autosizeOptions);
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [loading, apiRef, dataGridState, previousData?.ratelists, ratelists]);
  const exclude = pluck("id", ratelists ?? []);

  return (
    <Box sx={{ display: "flex", flexDirection: "column" }}>
      <DataGridPro
        apiRef={apiRef}
        autosizeOnMount={isNil(dataGridState)}
        autosizeOptions={autosizeOptions}
        columns={columns}
        disableColumnFilter
        disableColumnMenu
        disableColumnReorder
        disableRowSelectionOnClick
        disableVirtualization
        getRowClassName={rowClassName}
        getRowHeight={() => "auto"}
        hideFooter
        initialState={dataGridState}
        loading={loading || mutating}
        pinnedColumns={pinnedColumns}
        showToolbar
        slotProps={slotProps}
        slots={slots}
        sx={{
          "& .MuiDataGrid-row": {
            minHeight: "44px !important",
            "&:hover": {
              minHeight: "44px !important",
            },
          },
          "& .MuiDataGrid-cell": {
            minHeight: "44px !important",
          },
        }}
      />
      <RevisionDialog
        onClose={() => setRevisionOpenId(null)}
        open={!!revisionOpenId}
        ratelistId={revisionOpenId}
        type={type}
      />
      {companyId && (
        <AddRatelistDialog
          companyId={companyId}
          exclude={exclude}
          onAdded={() => refetch()}
          onClose={() => setAddDialogOpen(false)}
          open={addDialogOpen}
          type={type}
        />
      )}
    </Box>
  );
};
